<template>

  <v-container fluid fill-height ma-0 pa-0>

    <scroll-table
      v-if="descriptor.model == 'table'"
      :data="getTable"
      :sorted="sort"
      :sortable="!descriptor.autorefreshTable"
      :reverseRows="descriptor.reverseRows"
      :subscribed="descriptor.subscribed"
      :ownStatus="descriptor.ownStatus"
      :rowStatus="descriptor.rowStatus"
      :titled="descriptor.titled"
      :translate="descriptor.translate"
      :toolbar="descriptor.toolbar"
      :rowclick="descriptor.rowclick"      
      :removeble="descriptor.removeble"
      :resizable="descriptor.resizable"
      :header="descriptor.header"     
      :saveButton="descriptor.saveButton"
      :addButton="descriptor.addButton"
      :activeActions="descriptor.activeActions"
      :hideState="descriptor.hideState"
      :hideValues="descriptor.hideValues"
      @sort="(item) => sortTable(item)"
      @select="(payload) => {$emit('select', payload)}"
      @value="(row, item) => {$emit('value', row, item)}"
      @addForeign="(payload) => {$emit('addForeign', payload)}"
      @updateMeasures="(payload) => {$emit('updateMeasures', payload)}"
      @confirm="(payload) => {$emit('confirm', payload)}"
      @remove="(payload) => {$emit('remove', payload)}"
    />

    <v-dialog v-model="pdfDialogShow" persistent max-width="80%" v-if="pdfDialogShow">
      <form-pdf-reports
        :table="getTable"
        @close="onPdfReportsClose()"
      />
    </v-dialog>

  </v-container>

</template>

<script lang="ts">
import Vue from 'vue'
import scrollTable from '@/nms/components/partials/scrolltable.vue'
import FormPdfReports from '@/nms/pages/forms/form-pdf-reports.vue'

// Типы
interface DescriptorColumn {
  hide?: boolean
  value?: string
  title?: string
  [key: string]: any
}

interface DescriptorData {
  columns: DescriptorColumn[]
  pageLimit?: number
  nextPageButton?: boolean
  [key: string]: any
}

interface TableRow {
  name?: string
  path?: string
  n_path?: string
  maintenance_company_name?: string
  crumbs?: string
  [key: string]: any
}

interface TableData {
  columns: DescriptorColumn[]
  rows: TableRow[]
}

interface DialogState {
  pdf: {
    show: boolean
  }
}

interface FilterColumns {
  [key: string]: any
}

export default Vue.extend({
  name: 'DESCRIPTOR_WRAPPER',

  components: {
    scrollTable,
    FormPdfReports
  },

  props: {
    data: {
      type: Array as () => TableRow[],
      default: () => []
    },
    filter: {
      type: Object as () => any,
      default: () => ({})
    },
    descriptor: {
      type: Object as () => DescriptorData,
      default: () => ({})
    },
    open: {
      type: Boolean,
      default: () => false
    },
    pdfDialogShow: {
      type: Boolean,
      default: () => false
    },
  },

  data(): {
    dialog: DialogState
    search: string
    sort: string
    filterColumns: FilterColumns
    filterPath: string
    table: TableData
    description?: DescriptorData
  } {
    return {
      dialog: {
        pdf: {
          show: false
        }
      },
      search: '',
      sort: '',
      filterColumns: {},
      filterPath: 'showFullPath',
      table: {
        columns: [],
        rows: []
      },
    }
  },

  computed: {
    getData(): TableRow[] {
      return this.data
    },

    getDescriptor(): DescriptorData {
      return this.descriptor
    },

    getTable(): TableData {
      let t: TableData
      
      if (this.filterPath) {
        if (this.filterPath === 'showFullPath') {
          const t$ = this.$merge({}, this.table)
          t$.rows.forEach(row => {
            if (row?.path) {
              row.path = row.path.split('/').slice(1).join('/')
            }
          })
          t = t$
        } else if (this.filterPath === 'hideRoot') {
          const t$ = this.$merge({}, this.table)
          t$.rows.forEach(row => {
            if (row.path) {
              row.path = row.path.split('/').slice(2).join('/')
            }
          })
          t = t$
        } else if (this.filterPath === 'showL2orNearestFacility') {
          const t$ = this.$merge({}, this.table)
          t$.rows.forEach(row => {
            if (row.path) {
              const path$ = row.path.split('/').slice(1)
            }
          })
          t = t$
        } else if (this.filterPath === 'showNearestFacility') {
          const t$ = this.$merge({}, this.table)
          t$.rows.forEach(row => {
            if (row.n_path) {
              row.path = row.n_path
            } else {
              row.path = this.$getTranslate('no nearest facility')
            }
          })
          t = t$
        } else {
          t = this.$merge({}, this.table)
        }
      } else {
        t = this.$merge({}, this.table)
      }

      // Фильтрация по поиску
      if (this.search.length) {
        const searchLower = this.search.toLowerCase()
        t.rows = t.rows.filter(row => {
          let f = false
          if (row.name && row.name.toLowerCase().indexOf(searchLower) > -1) f = true
          if (row.maintenance_company_name && row.maintenance_company_name.toLowerCase().indexOf(searchLower) > -1) f = true
          if (row.crumbs && row.crumbs.toLowerCase().indexOf(searchLower) > -1) f = true
          return f
        })
      }

      // Сортировка
      if (this.sort) {
        const isDescending = this.sort.substring(0, 1) === '-'
        const fieldName = isDescending ? this.sort.substring(1) : this.sort
        
        t.rows = [...t.rows].sort((a, b) => {
          let valA = a[fieldName]
          let valB = b[fieldName]
          
          if (valA === null || valA === undefined) return isDescending ? -1 : 1
          if (valB === null || valB === undefined) return isDescending ? 1 : -1
          
          const isDateA = typeof valA === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(valA)
          const isDateB = typeof valB === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(valB)
          
          if (isDateA && isDateB) {
            valA = new Date(valA).getTime()
            valB = new Date(valB).getTime()
          } else if (typeof valA === 'string' && typeof valB === 'string') {
            valA = valA.toLowerCase()
            valB = valB.toLowerCase()
          }
          
          if (valA === valB) return 0
          
          if (isDescending) {
            return valA > valB ? -1 : 1
          } else {
            return valA < valB ? -1 : 1
          }
        })
      }
      
      return t
    },
  },

  watch: {
    data: {
      handler(newVal: TableRow[]): void {
        if (!newVal) return
        this.setup()
      },
      deep: true
    },
  },

  created(): void {
    this.setup()
  },

  methods: {
    async setup(): Promise<void> {
      this.description = this.descriptor
      this.reset()
      this.prepareColumns()
      this.prepareRows()
    },

    prepareColumns(): void {
      const columns = this.descriptor.columns.filter((column: DescriptorColumn) => !column.hide)
      this.$set(this.table, 'columns', columns)
    },

    prepareRows(): void {
      if (this.getData.length) {
        this.getData.forEach((row: TableRow) => {
          this.table.rows.push(row)
        })
      }
      
      if (this.table.rows.length < (this.description?.pageLimit || 0)) {
        if (this.description) this.description.nextPageButton = false
      } else {
        if (this.description) this.description.nextPageButton = true
      }
    },

    reset(): void {
      this.table = {
        columns: [],
        rows: []
      }
    },

    sortTable(item: DescriptorColumn): void {
      console.log(this.$options.name, 'sortTable', 'item', item)
      console.log(this.$options.name, 'sortTable', 'this.sort', this.sort)
      console.log(this.$options.name, 'sortTable', 'this.getTable', this.getTable)
      
      if (this.sort === item.value) {
        this.sort = '-' + item.value
      } else {
        this.sort = item.value || ''
      }
    },

    onPdfReportsClose(): void {
      this.dialog.pdf.show = false
      this.$emit('pdfReportsClose', true)
    },

    csvExport(): void {
      // console.log(this.$options.name, 'csvExport', 'this.getTable', this.getTable)
      let name = this.getDescriptor?.title || ''
      this.$csvExport(this.getTable, name, name)
      return
    },

    generatePDF(): void {
      this.$generatePDF(
        this.getTable,  // объект таблицы с rows и columns
        {
          cls: 'journal',
          name: this.getDescriptor?.title || ''
        },      // или объект вида {cls: String || '', type: String || '', name: String || ''}
        null,           // caller (опционально)
        {
          orientation: 'landscape',  // альбомная ориентация
          format: 'a4',              // формат A4
          font: 'MyFont'             // кастомный шрифт
        }
      )
      return
    }
  },
})
</script>

<style>
</style>
